import React, { useState, useEffect } from 'react';
import { Icon } from '@iconify/react';
import { toast } from 'react-toastify';
import { getData, postData, deleteData, uploadFile } from '../../../webService/webservice.js';
import '../../../styles/Auth/appearance.css';
import { Box, Modal, Button, Typography } from '@mui/material';

const LIMIT_OPTIONS = [5, 10, 25, "all"];

const StockImagesPage = () => {
    const [images, setImages] = useState([]);
    const [isUploading, setIsUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState({ current: 0, total: 0 });
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalItems, setTotalItems] = useState(0);
    const [limit, setLimit] = useState(5);
    const [loading, setLoading] = useState(true);
    const [open, setOpen] = useState(false);
    const [imageIndexToDelete, setImageIndexToDelete] = useState(null);
    const sessionToken = localStorage.getItem("Token");

    useEffect(() => {
        fetchImages(currentPage);
    }, [currentPage, limit]);

    const fetchImages = async (page = 1) => {
        try {
            setLoading(true);
            const res = await getData("stockimages/stock-images", {
                page,
                limit: limit === "all" ? 1000 : limit
            });

            if (res?.status === 200) {
                const data = res.data;
                setImages(data?.results || []);
                setCurrentPage(data?.currentPage || page);
                setTotalPages(data?.totalPages || 1);
                setTotalItems(data?.totalItems || 0);
            }
        } catch (err) {
            console.error("Fetch error:", err);
            toast.error("Failed to load stock images");
        } finally {
            setLoading(false);
        }
    };

    const handleFileUpload = async (event) => {
        const selectedFiles = Array.from(event.target.files || []);
        if (selectedFiles.length === 0) return;

        const allowedExtensions = /(\.jpg|\.jpeg|\.png)$/i;
        const maxSize = 1 * 1024 * 1024; // 1MB Limit

        const validFiles = [];
        const invalidFiles = [];

        selectedFiles.forEach((file) => {
            if (!allowedExtensions.exec(file.name)) {
                invalidFiles.push(`${file.name} (JPG/JPEG/PNG only)`);
            } else if (file.size > maxSize) {
                invalidFiles.push(`${file.name} (Exceeds 1MB limit)`);
            } else {
                validFiles.push(file);
            }
        });

        if (invalidFiles.length > 0) {
            toast.warn(`Skipped ${invalidFiles.length} file(s):\n${invalidFiles.join(', ')}`);
        }

        if (validFiles.length === 0) {
            if (event.target) event.target.value = "";
            return;
        }

        setIsUploading(true);
        setUploadProgress({ current: 0, total: validFiles.length });

        try {
            const userId = localStorage.getItem("userId");
            if (!userId) {
                toast.error("User not logged in");
                return;
            }

            const uploadedFileObjects = [];

            for (let i = 0; i < validFiles.length; i++) {
                const file = validFiles[i];
                setUploadProgress({ current: i + 1, total: validFiles.length });

                const uploadRes = await uploadFile(`stockimages/stock-images/upload/${userId}`, file);

                if (uploadRes?.status === 200 || uploadRes?.data) {
                    uploadedFileObjects.push(uploadRes.data);
                } else {
                    console.error(`Failed to upload ${file.name}:`, uploadRes);
                    toast.error(`Failed to upload ${file.name}`);
                }
            }

            if (uploadedFileObjects.length > 0) {
                const response = await postData("stockimages/stock-images/create", {
                    fileObject: uploadedFileObjects[0],
                    fileObjects: uploadedFileObjects,
                }, sessionToken);

                if (response?.success === false) {
                    toast.error(response.message || "Failed to save stock images");
                    return;
                }

                toast.success(`${uploadedFileObjects.length} stock image(s) uploaded successfully`);
                fetchImages(currentPage);
            }

        } catch (error) {
            console.error("Upload error:", error);
            toast.error("An unexpected error occurred during upload.");
        } finally {
            setIsUploading(false);
            setUploadProgress({ current: 0, total: 0 });
            if (event.target) event.target.value = "";
        }
    };

    const handleOpenDeleteModal = (frontendIndex) => {
        const originalIndex = totalItems - 1 - frontendIndex;
        setImageIndexToDelete(originalIndex);
        setOpen(true);
    };

    const handleCloseDeleteModal = () => {
        setOpen(false);
        setImageIndexToDelete(null);
    };

    const handleDelete = async () => {
        const indexToDelete = imageIndexToDelete;
        handleCloseDeleteModal();
        try {
            const res = await deleteData(`stockimages/stock-images/${indexToDelete}`, sessionToken);

            if (res?.success) {
                toast.success("Image removed");
                const newPage = images.length === 1 && currentPage > 1 ? currentPage - 1 : currentPage;
                fetchImages(newPage);
            } else {
                toast.error("Delete failed");
            }
        } catch (err) {
            console.error("Delete error:", err);
            toast.error("Delete failed");
        }
    };

    const getImageUrl = (img) => {
        const uri = img.uri || img.fileObject?.uri;
        if (!uri) return "";
        const baseUrl = (process.env.REACT_APP_API_URL || "").replace(/\/+$/, "");
        return `${baseUrl}/${uri.replace(/^\/+/, "")}`;
    };

    const handlePageChange = (direction) => {
        setCurrentPage((prev) => {
            if (direction === "first") return 1;
            if (direction === "last") return totalPages;
            if (direction === "next") return Math.min(prev + 1, totalPages);
            if (direction === "prev") return Math.max(prev - 1, 1);
            return prev;
        });
    };

    const handleItemsPerPageChange = (e) => {
        const value = e.target.value;
        setLimit(value === "all" ? "all" : Number(value));
        setCurrentPage(1);
    };

    const startItem = totalItems === 0 ? 0 : (currentPage - 1) * (limit === "all" ? totalItems : limit) + 1;
    const endItem = limit === "all" ? totalItems : Math.min(currentPage * limit, totalItems);

    return (
        <div className="appearance-layout">
            <div className="appearance-card">
                {/* Header */}
                <div className="appearance-header">
                    <div className="title-area">
                        <h2>Stock Images</h2>
                        <p>
                            Manage default stock images for the system
                            {totalItems > 0 && (
                                <span style={{ marginLeft: 8, color: '#94a3b8', fontWeight: 500, fontSize: 13 }}>
                                    — {totalItems} image{totalItems !== 1 ? 's' : ''} total
                                </span>
                            )}
                        </p>
                    </div>
                </div>

                {/* Upload */}
                <div className="upload-section">
                    <label className="upload-placeholder" style={{ opacity: isUploading ? 0.7 : 1 }}>
                        <Icon icon={isUploading ? "mdi:loading" : "mdi:cloud-upload-outline"}
                            className="icon"
                            style={isUploading ? { animation: "spin 0.8s linear infinite" } : {}}
                        />
                        <span>
                            {isUploading
                                ? `Uploading image ${uploadProgress.current} of ${uploadProgress.total}...`
                                : "Click to upload stock images (Select multiple files at once, Max 1MB each, JPEG/JPG/PNG only)"}
                        </span>
                        <input
                            type="file"
                            accept=".jpeg,.jpg,.png"
                            multiple
                            hidden
                            onChange={handleFileUpload}
                            disabled={isUploading}
                        />
                    </label>
                </div>

                {/* Grid */}
                {loading ? (
                    <div style={{ textAlign: 'center', padding: '60px 20px', color: '#94a3b8', fontSize: 15 }}>
                        <Icon icon="mdi:loading" style={{ fontSize: 36, animation: "spin 0.8s linear infinite", display: 'block', margin: '0 auto 12px' }} />
                        Loading images...
                    </div>
                ) : images.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '60px 20px', color: '#94a3b8', fontSize: 15 }}>
                        <Icon icon="mdi:image-off-outline" style={{ fontSize: 48, color: '#cbd5e1', display: 'block', margin: '0 auto 12px' }} />
                        No stock images uploaded yet
                    </div>
                ) : (
                    <div className="backgrounds-grid">
                        {images.map((img, index) => {
                            const imageUrl = getImageUrl(img);
                            return (
                                <div
                                    key={img._id}
                                    className="background-item"
                                    style={{
                                        backgroundImage: imageUrl ? `url("${imageUrl}")` : "none",
                                        backgroundColor: imageUrl ? "transparent" : "#f0f0f0",
                                        backgroundSize: "cover",
                                        backgroundPosition: "center",
                                        backgroundRepeat: "no-repeat"
                                    }}
                                >
                                    <div className="background-overlay">
                                        <button
                                            className="delete-bg-btn"
                                            onClick={() => handleOpenDeleteModal(index)}
                                        >
                                            Remove
                                        </button>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}

                {/* Pagination (Like Category Page) */}
                {!loading && totalItems > 0 && (
                    <div className="category-bottom" style={{ borderTop: '1px solid #f1f5f9', marginTop: 24 }}>
                        <div className="category-section show-entries">
                            <span className="text-label" style={{ color: 'rgba(0, 0, 0, 0.55)', fontSize: 14, fontWeight: 600 }}>Show</span>
                            <select
                                className="entries-select"
                                value={limit}
                                onChange={handleItemsPerPageChange}
                                style={{ margin: '0 6px', padding: '4px 6px', fontSize: 14, borderRadius: 10, border: '1px solid #ccc' }}
                            >
                                {LIMIT_OPTIONS.map(opt => (
                                    <option key={opt} value={opt}>{opt === "all" ? "All" : opt}</option>
                                ))}
                            </select>
                            <span className="text-label" style={{ color: 'rgba(0, 0, 0, 0.55)', fontSize: 14, fontWeight: 600 }}>Entries</span>
                        </div>

                        <div className="showing-range" style={{ color: '#525252', fontSize: 14, fontWeight: 600 }}>
                            {limit === "all"
                                ? `Showing all ${totalItems} entries`
                                : `Showing ${startItem} to ${endItem} of ${totalItems} entries`}
                        </div>

                        <div className="pagination-buttons" style={{ display: 'flex', gap: 12 }}>
                            <PaginationBtn
                                onClick={() => handlePageChange("first")}
                                disabled={currentPage === 1}
                                title="First"
                            >
                                <Icon icon="mdi:chevron-double-left" />
                            </PaginationBtn>
                            <PaginationBtn
                                onClick={() => handlePageChange("prev")}
                                disabled={currentPage === 1}
                                title="Previous"
                            >
                                <Icon icon="mdi:chevron-left" />
                            </PaginationBtn>
                            <PaginationBtn
                                onClick={() => handlePageChange("next")}
                                disabled={currentPage === totalPages}
                                title="Next"
                            >
                                <Icon icon="mdi:chevron-right" />
                            </PaginationBtn>
                            <PaginationBtn
                                onClick={() => handlePageChange("last")}
                                disabled={currentPage === totalPages}
                                title="Last"
                            >
                                <Icon icon="mdi:chevron-double-right" />
                            </PaginationBtn>
                        </div>
                    </div>
                )}

                <Modal open={open} onClose={handleCloseDeleteModal}>
                    <Box sx={modalStyle}>
                        <Box sx={deleteIconBadgeStyle}>🗑️</Box>
                        <Typography variant="h6" sx={{ fontWeight: 700, color: "#2b2b2b" }}>Confirm Remove</Typography>
                        <Typography sx={{ mt: 1.2, fontSize: 14, color: "#666", lineHeight: 1.6 }}>Are you sure you want to remove this stock image?</Typography>
                        <Box sx={{ display: "flex", justifyContent: "center", gap: 2.5, mt: 3.5 }}>
                            <Button variant="contained" onClick={handleDelete} className="add-Product-button">Yes, Remove</Button>
                            <Button variant="outlined" onClick={handleCloseDeleteModal} className="cancel-Product-button">Cancel</Button>
                        </Box>
                    </Box>
                </Modal>
            </div>

            <style>{`
                @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
                @keyframes deletePulse {
                    0% { transform: scale(1); boxShadow: 0 0 0 0 rgba(60, 0, 8, 0.4); }
                    70% { transform: scale(1.05); boxShadow: 0 0 0 15px rgba(60, 0, 8, 0); }
                    100% { transform: scale(1); boxShadow: 0 0 0 0 rgba(60, 0, 8, 0); }
                }
            `}</style>
        </div>
    );
};

const PaginationBtn = ({ children, onClick, disabled, title }) => (
    <button
        onClick={onClick}
        disabled={disabled}
        title={title}
        style={{
            minWidth: 38,
            height: 38,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            border: 'none',
            borderRadius: '18px',
            background: disabled ? '#e2e8f0' : 'linear-gradient(#3C0008, #a52a2a)',
            color: disabled ? '#3C0008' : '#ffffff',
            cursor: disabled ? 'not-allowed' : 'pointer',
            fontSize: 18,
            transition: 'all 0.3s ease',
            boxShadow: disabled ? 'none' : '0 2px 8px rgba(60, 0, 8, 0.2)'
        }}
    >
        {children}
    </button>
);

const modalStyle = {
    position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)",
    width: 380, background: "linear-gradient(135deg, #ffffff, #faf5f6)", borderRadius: "20px",
    boxShadow: "0 25px 70px rgba(0,0,0,0.35)", p: 4, outline: "none", textAlign: "center"
};

const deleteIconBadgeStyle = {
    width: 74, height: 74, borderRadius: "50%", background: "#3C0008",
    display: "flex", alignItems: "center", justifyContent: "center",
    margin: "0 auto 16px auto", color: "#fff", fontSize: 30,
    boxShadow: "0 12px 28px rgba(60, 0, 8, 0.3)", animation: "deletePulse 1.6s infinite ease-in-out"
};

export default StockImagesPage;
